import React, { useEffect } from 'react'
import { useForm } from 'react-hook-form'

import { selectedEvent, selectPublishedEvent } from '../../../../store/events/eventsSlice'
import { useAppSelector } from '../../../../store/hooks'

import { RequiredFieldRow } from '../../../../pages/shows/detail/components/RequiredFieldRow'
import { RequiredFieldsHeader } from '../../../../pages/shows/detail/components/RequiredFieldsHeader'
import EventFormFooterCommonComponent from '../../../common/buttons/EventFormFooterCommonComponent'
import { IconHorseImg } from '../../../icons/IconHorseImg'
import { IconUser } from '../../../icons/IconUser'
import EventFormContainerComponent from '../container/EventFormContainerComponent'
import EventFormHeaderComponent from '../header/EventFormHeaderComponent'

import useGetEventData from '../../../../hooks/users/common/useGetEventData'

import { IEventInterface } from '../../../../models/events/event.interface'
import { EventModel } from '../../../../models/events/event.model'

import { REQUIRED_FIELDS } from '../../../../const/required-fields-const'

interface IProps {
  data: any
  validFormHandler: any
  invalidFormHandler: any
  onInvalidAndExit: any
  nextEventTab?: string
  eventTab?: string
}

const eventRequiredFieldDefaultValues = new EventModel().toObject()

export const EventRequiredFieldFormComponent: React.FC<IProps> = (props) => {
  const { getAllData } = useGetEventData()

  const publishedEvent = useAppSelector(selectPublishedEvent)
  const event = useAppSelector(selectedEvent)

  const { handleSubmit, reset, watch, setValue } = useForm<IEventInterface>({
    defaultValues: { ...eventRequiredFieldDefaultValues },
    mode: 'onChange',
  })

  useEffect(() => {
    if (event.Event) {
      reset({
        ...event.Event,
        requiredFields: event.Event.requiredFields,
        requiredHorseFields: event.Event.requiredHorseFields,
      })
    }
  }, [event])

  const clickUserHandler = (value: string) => {
    if (watch('requiredFields').includes(value)) {
      setValue(
        'requiredFields',
        watch('requiredFields').filter((field) => field !== value)
      )
    } else {
      setValue('requiredFields', [...watch('requiredFields'), value])
    }
  }
  const clickHorseHandler = (value: string) => {
    if (watch('requiredHorseFields').includes(value)) {
      setValue(
        'requiredHorseFields',
        watch('requiredHorseFields').filter((field) => field !== value)
      )
    } else {
      setValue('requiredHorseFields', [...watch('requiredHorseFields'), value])
    }
  }

  useEffect(() => {
    if (event.Event?.id) getAllData(event.Event.id, ['v01_events']).then()
  }, [event.Event?.id])

  // Filter people numbers by discipline from peopleNumbers array
  const renderPeopleNumbersByDiscipline = () => {
    const groupByDiscipline = (discipline: string) => {
      return REQUIRED_FIELDS.peopleNumbers.filter((item) => item.discipline === discipline)
    }

    const disciplines = [
      { name: 'English', numbers: groupByDiscipline('English') },
      { name: 'Western', numbers: groupByDiscipline('Western') },
      { name: 'Rodeo', numbers: groupByDiscipline('Rodeo') },
      { name: 'College', numbers: groupByDiscipline('College') },
    ]

    return (
      <>
        {disciplines.map((discipline, index) => (
          <React.Fragment key={`discipline-${discipline.name}`}>
            {index > 0 && <div className="border-t my-4"></div>}

            <div className="font-medium text-gray-700 mb-3 ml-3">{discipline.name}</div>

            <div className="flex flex-col gap-3">
              {discipline.numbers.map((itm) => (
                <RequiredFieldRow
                  onClick={() => clickUserHandler(itm.value)}
                  key={`p-${itm.value}`}
                  src={itm.img}
                  title={itm.title}
                  isActive={watch('requiredFields').includes(itm.value)}
                />
              ))}
            </div>
          </React.Fragment>
        ))}
      </>
    )
  }

  // Filter horse numbers by discipline from horseNumbers array
  const renderHorseNumbersByDiscipline = () => {
    const groupByDiscipline = (discipline: string) => {
      return REQUIRED_FIELDS.horseNumbers.filter(
        (item) =>
          item.discipline === discipline &&
          item.value !== 'horseMicrochipNumber' &&
          item.value !== 'horseDNACaseNumber' &&
          item.value !== 'horsePassportNumber'
      )
    }

    const disciplines = [
      { name: 'English', numbers: groupByDiscipline('English') },
      { name: 'Western', numbers: groupByDiscipline('Western') },
      { name: 'Rodeo', numbers: groupByDiscipline('Rodeo') },
      { name: 'College', numbers: groupByDiscipline('College') },
    ]

    return (
      <>
        {disciplines.map((discipline, index) => (
          <React.Fragment key={`horse-discipline-${discipline.name}`}>
            {index > 0 && <div className="border-t my-4"></div>}

            <div className="font-medium text-gray-700 mb-3 ml-2">{discipline.name}</div>

            <div className="flex flex-col gap-3">
              {discipline.numbers.map((itm) => (
                <RequiredFieldRow
                  onClick={() => clickHorseHandler(itm.value)}
                  key={`h-${itm.value}`}
                  src={itm.img}
                  title={itm.title}
                  isActive={watch('requiredHorseFields').includes(itm.value)}
                />
              ))}
            </div>
          </React.Fragment>
        ))}
      </>
    )
  }

  const userReqFields = watch('requiredFields')
  const horseReqFields = watch('requiredHorseFields')

  return (
    <EventFormContainerComponent>
      <div className="mr-4 mb-4 w-full mx-auto">
        <EventFormHeaderComponent
          title="Required fields"
          description="Select the profile fields that are required to register for this event"
        >
          <EventFormFooterCommonComponent
            publishedEvent={publishedEvent}
            publishButton={false}
            loading={false}
            onSaveAndExit={(e, publishEvent) => {
              handleSubmit(
                (data) =>
                  publishEvent({
                    dataToSave: data,
                    onSaveAndExit: true,
                    tabName: 'EventRequiredFields',
                    validFormHandler: props.validFormHandler,
                  }),
                props.onInvalidAndExit
              )(e)
            }}
            onNext={(e, publishEvent) => {
              handleSubmit(
                (data) =>
                  publishEvent({
                    dataToSave: data,
                    tabName: 'EventRequiredFields',
                    validFormHandler: props.validFormHandler,
                  }),
                props.invalidFormHandler
              )(e)
            }}
          />
        </EventFormHeaderComponent>

        <div className="flex flex-col lg:flex-row flex-1 gap-5">
          <div className={'flex border flex-col rounded-md border-[#D3DAEE] p-4 flex-1 gap-3'}>
            <RequiredFieldsHeader
              title={'People'}
              desc={'Select required fields'}
              count={watch('requiredFields').length}
              icon={<IconUser />}
            />
            <div className={'flex flex-col gap-3 mt-3'}>
              {REQUIRED_FIELDS.people.map((itm) => (
                <RequiredFieldRow
                  onClick={() => clickUserHandler(itm.value)}
                  key={`p-${itm.value}`}
                  title={itm.title}
                  isActive={watch('requiredFields').includes(itm.value)}
                />
              ))}
            </div>

            <div className={'flex flex-col mt-3 border-t pt-4'}>
              {renderPeopleNumbersByDiscipline()}
            </div>
          </div>

          <div className={'flex border flex-col rounded-md border-[#D3DAEE] p-4 flex-1 gap-3'}>
            <RequiredFieldsHeader
              title={'Horses'}
              desc={'Select required fields'}
              count={watch('requiredHorseFields').length}
              icon={<IconHorseImg />}
            />

            <div className={'flex flex-col gap-3 mt-3'}>
              {REQUIRED_FIELDS.horseSecondary.map((itm) => (
                <RequiredFieldRow
                  key={`h-sec-${itm.value}`}
                  onClick={() => clickHorseHandler(itm.value)}
                  src={itm.img}
                  title={itm.title}
                  isActive={watch('requiredHorseFields').includes(itm.value)}
                />
              ))}
            </div>

            <div className={'flex flex-col gap-3 border-t pt-5 mt-3'}>
              {REQUIRED_FIELDS.horseMain.map((itm) => (
                <RequiredFieldRow
                  onClick={() => clickHorseHandler(itm.value)}
                  key={`h-main-${itm.value}`}
                  title={itm.title}
                  isActive={watch('requiredHorseFields').includes(itm.value)}
                />
              ))}
            </div>

            <div className={'flex flex-col gap-3 mt-3 border-t pt-5'}>
              {REQUIRED_FIELDS.horseNumbers.slice(0, 3).map((itm) => (
                <RequiredFieldRow
                  key={`h-id-${itm.value}`}
                  onClick={() => clickHorseHandler(itm.value)}
                  title={itm.title}
                  isActive={horseReqFields.includes(itm.value)}
                />
              ))}
            </div>

            <div className={'flex flex-col mt-4 border-t pt-5'}>
              {renderHorseNumbersByDiscipline()}
            </div>
          </div>
        </div>
      </div>
    </EventFormContainerComponent>
  )
}
